/**
 * Canonical seed placement order L(size) (§7.10):
 *   L(1) = [1];  L(2m) = for each x in L(m) emit [x, 2m+1−x].
 * size 8 → [1,8,4,5,2,7,3,6] (first-round pairings 1v8, 4v5, 2v7, 3v6).
 */
function canonicalOrder(size) {
    let order = [1];
    let m = 1;
    while (m < size) {
        const span = 2 * m + 1;
        const next = [];
        for (const x of order) {
            next.push(x, span - x);
        }
        order = next;
        m *= 2;
    }
    return order;
}
/** Least power of two ≥ n (n ≥ 1). */
function nextPowerOfTwo(n) {
    let size = 1;
    while (size < n)
        size *= 2;
    return size;
}
/** Placeholder game id for a generated spec (app remaps to a ULID on persist). */
function specId(round, position) {
    return `R${round}P${position}`;
}
/**
 * Generate a single-elimination bracket from a locked seed list (spec §7.10).
 *
 * winnerOf sources reference feeding games by the deterministic placeholder id
 * `R{round}P{position}`; the app maps these to ULIDs when persisting games.
 * Bye pruning: a first-round game whose weaker seed exceeds n is not created,
 * and the surviving (stronger) seed is promoted directly into its round-2 slot.
 */
export function generateBracket(seedTeamIds, advance) {
    const available = seedTeamIds.length;
    const n = advance === "all" ? available : Math.min(advance, available);
    if (n <= 1)
        return [];
    const size = nextPowerOfTwo(n);
    const rounds = Math.log2(size);
    const order = canonicalOrder(size);
    const specs = [];
    // Round 1: pair consecutive slots; prune games with a bye seed (> n).
    // survivor[position] = the promoted seed for a pruned round-1 game.
    const survivor = new Map();
    const firstRoundGames = size / 2;
    for (let p = 0; p < firstRoundGames; p++) {
        const a = order[2 * p];
        const b = order[2 * p + 1];
        const larger = Math.max(a, b);
        const smaller = Math.min(a, b);
        if (larger > n) {
            // Bye: the stronger seed advances without playing.
            survivor.set(p, smaller);
            continue;
        }
        specs.push({
            round: 1,
            position: p,
            homeSource: { kind: "seed", seed: a },
            awaySource: { kind: "seed", seed: b },
        });
    }
    // Rounds 2..log2(size): each game is fed by two prior-round games (positions
    // 2p and 2p+1). A feeding round-1 bye promotes its seed directly.
    for (let round = 2; round <= rounds; round++) {
        const gamesInRound = size / 2 ** round;
        for (let p = 0; p < gamesInRound; p++) {
            const feed = (fp) => {
                if (round === 2 && survivor.has(fp)) {
                    return { kind: "seed", seed: survivor.get(fp) };
                }
                return { kind: "winnerOf", gameId: specId(round - 1, fp) };
            };
            specs.push({
                round,
                position: p,
                homeSource: feed(2 * p),
                awaySource: feed(2 * p + 1),
            });
        }
    }
    return specs;
}
/**
 * Resolve every bracket slot to a team (or null) and find the champion
 * (spec §7.11). Seeds resolve from lockedSeeds when locked, else render
 * provisionally as "Seed k"; winnerOf resolves once the referenced game is
 * final/forfeit. Champion = resolved winner of the last (highest-round) game.
 *
 * Slot labels use the teamId when resolved (the UI maps id → name); otherwise
 * "Seed k" for a seed source or "Winner Gm N" for a winnerOf source, where N
 * is the referenced game's 1-based number (games ordered by round, position).
 */
export function resolveBracket(bracketGames, lockedSeeds) {
    const games = bracketGames.filter((g) => g.bracket !== undefined);
    if (games.length === 0)
        return { games: [], championId: null };
    const ordered = [...games].sort((x, y) => {
        const rx = x.bracket.round;
        const ry = y.bracket.round;
        if (rx !== ry)
            return rx - ry;
        return x.bracket.position - y.bracket.position;
    });
    const byId = new Map();
    const gameNumber = new Map();
    ordered.forEach((g, i) => {
        byId.set(g.id, g);
        gameNumber.set(g.id, i + 1);
    });
    const winnerCache = new Map();
    const inProgress = new Set();
    const resolveTeam = (source) => {
        if (source.kind === "seed") {
            if (lockedSeeds === null)
                return null;
            return lockedSeeds[source.seed - 1] ?? null;
        }
        const g = byId.get(source.gameId);
        if (!g)
            return null;
        return gameWinner(g);
    };
    function gameWinner(g) {
        if (winnerCache.has(g.id))
            return winnerCache.get(g.id);
        /* v8 ignore next */
        if (inProgress.has(g.id))
            return null;
        inProgress.add(g.id);
        let winner = null;
        if (g.status === "final" && g.homeScore !== undefined && g.awayScore !== undefined) {
            const homeTeam = resolveTeam(g.bracket.homeSource);
            const awayTeam = resolveTeam(g.bracket.awaySource);
            winner = g.homeScore > g.awayScore ? homeTeam : awayTeam;
        }
        else if (g.status === "forfeit" && g.forfeitTeamId) {
            const homeTeam = resolveTeam(g.bracket.homeSource);
            const awayTeam = resolveTeam(g.bracket.awaySource);
            if (homeTeam === g.forfeitTeamId)
                winner = awayTeam;
            else if (awayTeam === g.forfeitTeamId)
                winner = homeTeam;
            else
                winner = null;
        }
        inProgress.delete(g.id);
        winnerCache.set(g.id, winner);
        return winner;
    }
    const slotFor = (source) => {
        const teamId = resolveTeam(source);
        if (source.kind === "seed") {
            const provisional = lockedSeeds === null;
            return {
                source,
                teamId,
                provisional,
                label: teamId ?? `Seed ${source.seed}`,
            };
        }
        const num = gameNumber.get(source.gameId);
        return {
            source,
            teamId,
            provisional: false,
            label: teamId ?? `Winner Gm ${num ?? "?"}`,
        };
    };
    const resolvedGames = ordered.map((g) => ({
        gameId: g.id,
        round: g.bracket.round,
        position: g.bracket.position,
        gameNumber: gameNumber.get(g.id),
        home: slotFor(g.bracket.homeSource),
        away: slotFor(g.bracket.awaySource),
        winnerId: gameWinner(g),
        status: g.status,
    }));
    // Champion = winner of the last game (highest round; final has one game).
    const finalGame = ordered[ordered.length - 1];
    const championId = gameWinner(finalGame);
    return { games: resolvedGames, championId };
}
/**
 * The shape a bracket would take, without generating it (spec §7.10).
 *
 * The same arithmetic `generateBracket` runs, answered before there are seeds
 * to run it on: an admin choosing how many teams advance needs to see what the
 * choice produces — a 12-team division taking its top 8 plays 7 games over 3
 * rounds with nobody idle, while taking 6 gives the top two seeds a bye. That
 * question is asked from the division setup card, where no seed list exists
 * yet, so it takes a count rather than ids.
 *
 * `games` is `advancing - 1` because single elimination eliminates exactly one
 * team per game and stops at one champion — byes remove the game AND the
 * elimination, so the count already accounts for them. The engine's own tests
 * pin that against `generateBracket` for every size it supports.
 */
export function bracketShape(teamCount, advance) {
    const available = Math.max(0, Math.trunc(teamCount));
    const advancing = advance === "all" ? available : Math.min(Math.trunc(advance), available);
    // One team is a champion by default and zero is not a tournament; neither is
    // a bracket, and `generateBracket` creates no games for either.
    if (advancing <= 1) {
        return { advancing: Math.max(0, advancing), size: 0, byes: 0, rounds: 0, games: 0 };
    }
    const size = nextPowerOfTwo(advancing);
    return {
        advancing,
        size,
        byes: size - advancing,
        rounds: Math.log2(size),
        games: advancing - 1,
    };
}
//# sourceMappingURL=bracket.js.map